import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { Sidebar, SidebarBody, SidebarSection, SidebarItem, SidebarFooter } from '../Catalyst/sidebar';
import { Navbar } from '../Catalyst/navbar';
import { SidebarLayout } from '../Catalyst/sidebar-layout';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../Catalyst/dropdown';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchOrganizationHierarchyAsync, getUserAccountDetailsAsync } from '../../store/thunks/OrganizationThunk';
import { logout, setParentOrgId } from '../../store/slices/OrganizationSlice';
import FinleyIcon from '../../assets/FinleyIcon ';
import NavBarHomeIcon from '../../assets/NavBarHomeIcon';
import NavBarUserIcon from '../../assets/NavBarUserIcon';
import NavBarBuildingIcon from '../../assets/NavBarBuildingIcon';
import SettingsIcon from '../../assets/SettingsIcon';
import NavBarSearchIcon from '../../assets/NavBarSearchIcon';

export function AppLayout() {
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const { orgId } = params;
    
    const selectedParentOrgId = useSelector((state: RootState) => state.OrganizationListStore?.selectedParentOrgId);
    const parentHierarchy = useSelector((state: RootState) => state.OrganizationListStore?.parentHierarchyOrganizationList) ?? [];
    const userAccount = useSelector((state: RootState) => state.OrganizationListStore?.userAccount);

    // Get the current organization ID from params or selected org
    const currentOrgId = orgId || selectedParentOrgId;
    const orgPath = currentOrgId ? `/${currentOrgId}` : '';

    // Sync URL orgId to Redux so sidebar links persist across navigation
    useEffect(() => {
        if (orgId) {
            dispatch(setParentOrgId(Number(orgId)));
        }
    }, [orgId, dispatch]);

    useEffect(() => {
        if (currentOrgId) {
            dispatch(fetchOrganizationHierarchyAsync(Number(currentOrgId)));
        }
    }, [currentOrgId, dispatch]);

    // Fetch user account details on mount
    useEffect(() => {
        if (!userAccount) {
            dispatch(getUserAccountDetailsAsync());
        }
    }, [dispatch, userAccount]);

    const handleLogout = () => {
        // Clear localStorage
        localStorage.removeItem('accessToken');
        // Clear Redux state
        dispatch(logout());
        // Navigate to login
        navigate('/');
    };

    const userName = userAccount?.first && userAccount?.last 
        ? `${userAccount.first} ${userAccount.last}`
        : userAccount?.email || 'User';

    return (
        <SidebarLayout
            navbar={
                <Navbar>
                    {/* Future navbar content */}
                </Navbar>
            }
            sidebar={
                <Sidebar className='bg-white flex flex-col pt-6 border-r-2 w-[80px]'>
                    <div className="flex justify-center mb-6">
                        <FinleyIcon />
                    </div>
                    
                    <SidebarBody className="flex-1">
                        <SidebarSection className="flex flex-col gap-2">
                            {/* Home/Dashboard */}
                            <div className="relative group">
                                    <SidebarItem 
                                        href={`/org-home${orgPath}`}
                                        className={(location.pathname.startsWith('/org-home') ? 'bg-[#F5F5F5] rounded-md ' : '') + ' flex items-center justify-center'}
                                        title="Home"
                                    >
                                        <span className="flex items-center justify-center w-full h-full"><NavBarHomeIcon /></span>
                                    </SidebarItem>
                            </div>

                            {/* User Management */}
                                <div className="relative group">
                                        <SidebarItem 
                                            href={`/user-list-dashboard${orgPath}`}
                                            className={
                                                (location.pathname.startsWith('/user-list-dashboard') ? 'bg-[#F5F5F5] rounded-md ' : '') +
                                                ' flex items-center justify-center '
                                            }
                                            title="User Management"
                                        >
                                            <span className="flex items-center justify-center w-full h-full"><NavBarUserIcon /></span>
                                        </SidebarItem>
                                </div>

                            {/* Organization Management */}
                            <SidebarItem
                                href={`/org-view${orgPath}`}
                                className={(location.pathname.startsWith('/org-view') ? 'bg-[#F5F5F5] rounded-md ' : '') + ' flex items-center justify-center'}
                                title="Organization Management"
                            >
                                <span className="flex items-center justify-center w-full h-full"><NavBarBuildingIcon /></span>
                            </SidebarItem>

                            {/* Global Search */}
                            <SidebarItem
                                href="/search"
                                className={(location.pathname.startsWith('/search') ? 'bg-[#F5F5F5] rounded-md ' : '') + ' flex items-center justify-center'}
                                title="Search"
                            >
                                <span className="flex items-center justify-center w-full h-full"><NavBarSearchIcon /></span>
                            </SidebarItem>
                        </SidebarSection>
                    </SidebarBody>
                    
                    <SidebarFooter>
                        <SidebarSection>
                            {/* User Settings Dropdown */}
                            <Dropdown>
                                <DropdownButton
                                    plain
                                    className="flex items-center justify-center w-full p-2 rounded-md hover:bg-[#F5F5F5] transition-colors bg-transparent border-none"
                                    title={`Settings - ${userName}`}
                                >
                                    <SettingsIcon />
                                </DropdownButton>
                                <DropdownMenu className="min-w-[180px]">
                                    <div className="px-3 py-2 border-b border-gray-200">
                                        <p className="text-sm font-medium text-gray-900">{userName}</p>
                                        {userAccount?.email && (
                                            <p className="text-xs text-gray-500">{userAccount.email}</p>
                                        )}
                                    </div>
                                    <DropdownItem onClick={handleLogout}>
                                        Logout
                                    </DropdownItem>
                                </DropdownMenu>
                            </Dropdown>
                        </SidebarSection>
                    </SidebarFooter>
                </Sidebar>
            }
        >
            <div className="w-full">
                <Outlet />
            </div>
        </SidebarLayout>
    );
}